import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { ScheduleRequestAddress } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v2/calendar_events.pb';
import { Workstation } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { i18next } from '@frontend/i18n';
import { titleCase } from '@frontend/string';
import { TagType } from '@frontend/design-system';
import { toTitleCase } from '../../../../../utils';

dayjs.extend(customParseFormat);

export const getFormattedScheduleRequestAddress = (address?: ScheduleRequestAddress): string => {
  if (!address) return '-';

  const { unit, street, postalCode, city, state } = address;
  const addressUnit = unit ? `${unit},` : '';
  const addressStreet = street ? `${street},` : '';
  const addressCity = city ? `${city},` : '';
  const addressState = state ? `${state},` : '';
  const addressPostalCode = postalCode ? `${postalCode}` : '';
  const formattedAddress = `${addressUnit} ${addressStreet} ${addressCity} ${addressState} ${addressPostalCode}`;

  return !formattedAddress.trim() ? '-' : formattedAddress;
};

export const getScheduleRequestBirthDate = (birthDate?: string): string => {
  if (!birthDate) return '-';
  return dayjs(birthDate).format('MM-DD-YYYY');
};

type tagKeys =
  | '{appt_datetime}'
  | '{date}'
  | '{time}'
  | '{first_name}'
  | '{last_name}'
  | '{preferred_name}'
  | '{practice_name}';

interface RequestTagsType {
  list: TagType[];
  map: { [k in tagKeys]: string };
}

export const getRequestTags = (
  dateString: string,
  schedulee: ScheduleRequest['schedulee'],
  practiceName: string
): RequestTagsType => {
  const apptDateTime = `${dayjs(dateString).format('dddd, MMM Do')} ${dayjs(dateString).format('h:mma')}`;

  const map: { [k in tagKeys]: string } = {
    '{appt_datetime}': apptDateTime,
    '{date}': dayjs(dateString).format('ddd, MMM Do'),
    '{time}': dayjs(dateString).format('h:mma'),
    '{preferred_name}': schedulee?.firstName || '',
    '{first_name}': schedulee?.firstName || '',
    '{last_name}': schedulee?.lastName || '',
    '{practice_name}': practiceName ?? '',
  };
  const list: TagType[] = Object.entries(map).map(([key, value]) => ({
    key: key,
    label: titleCase(key.replace(/{([^}]+)}/, (_, keyStr) => keyStr.replace(/_/g, ' '))),
    value,
  }));
  return { list, map };
};

const buildWorkstationListFromName = (nameList: string[], workstations: Workstation[]) =>
  nameList.reduce((prev, name: string) => {
    const workstation = workstations.find(
      ({ resourceName, externalId }) => resourceName === name || externalId === name
    );
    if (workstation?.id) {
      return [...prev, workstation.id];
    }
    return prev;
  }, [] as string[]);

const checkIsWorkstationId = (nameList: string[], workstations: Workstation[]) => {
  for (const asset of workstations) {
    const value = nameList.findIndex((name) => asset.id === name);
    if (value > -1) {
      return asset.id;
    }
  }
  return false;
};

export const getWorkstationId = (nameList: string[], workstations: Workstation[]) => {
  const workstationId = checkIsWorkstationId(nameList, workstations);
  if (workstationId) {
    return workstationId;
  }
  const list = buildWorkstationListFromName(nameList, workstations);
  return list?.[0] ?? '';
};

export const getDurationInMinutes = (duration: string | number): number => {
  if (typeof duration === 'string') {
    const reg = /[hms]/g;
    const [unit] = duration.match(reg) || ['m'];
    switch (unit) {
      case 's':
        return Number.parseInt(duration) / 60;
      case 'h':
        return Number.parseInt(duration) * 60;
      case 'm':
      default:
        return Number.parseInt(duration);
    }
  }
  // if duration comes in as a number it is assumed to be in minutes
  return duration;
};

const templateTagRegex = new RegExp(/\{(.*?)\}/gm);

/**
 * @desc: Method is defined to replace the template tags with the actual values
 * @param text : string (template string with tags)
 * @param tagValueMap : Object (key value pair of tag and value)
 * @returns : string
 */
export const replaceTemplateTags = (text: string, tagValueMap: Record<string, string>): string => {
  return text.replace(templateTagRegex, (match) => {
    const tag = match.replace(/{{\s*|\s*}}/g, '');
    return tagValueMap[tag] || match;
  });
};

export const convertToServerDate = (date: string): string => dayjs(date).format('YYYY-MM-DD');

const formatCustomFieldValue = (value?: string | boolean) => {
  if (value === true) return i18next.t('Yes', { ns: 'scheduleCalendarRequest' });
  if (value === false) return i18next.t('No', { ns: 'scheduleCalendarRequest' });
  return value ? String(value) : '-';
};

/**
 * Parses a JSON string from schedule request's custom field data into a custom field data object while removing unnecessary fields.
 *
 * @param str - The JSON string representing custom field data to be parsed.
 * @returns An object containing the parsed custom field data with specified keys removed, or an empty object if parsing fails or if no input is provided.
 */
export const getCustomFieldData = (str?: string, omitFields: string[] = []): Record<string, string> => {
  if (!str) return {};

  try {
    const result: Record<string, string> = {};
    const data = JSON.parse(str) as Record<string, string | boolean>;

    // Remove unnecessary fields - `ai_request_data` is used for AIR requests
    ['ai_request_data', ...omitFields].forEach((key) => {
      delete data[key];
    });

    // Format the field names and their values
    Object.entries(data).forEach(([key, value]) => {
      const formattedKey = toTitleCase(key);
      result[formattedKey] = formatCustomFieldValue(value);
    });

    return result;
  } catch (e) {
    console.error(e);
    return {};
  }
};

// Removes duplicate entities from an array based on their id field
export const getDistinctEntities = <T extends { id?: string }>(entities: T[]): T[] => {
  if (!entities || entities.length === 0) return [];

  const entityMap = new Map<string, T>();

  entities.forEach((entity) => {
    if (entity.id && !entityMap.has(entity.id)) {
      entityMap.set(entity.id, entity);
    }
  });

  return Array.from(entityMap.values());
};
